import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import ConveyorGauge from "./ConveyorGauge";
import EquipmentDashboardModal from "./EquipmentDashboardModal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Warning, Plus, Trash, Wrench, ChartLineUp, Lightning, MagicWand, ChatCircleDots, ArrowsClockwise } from "@phosphor-icons/react";

const PERIODS = [
    { key: "vessel", label: "Current vessel" },
    { key: "24h", label: "Last 24h" },
    { key: "7d", label: "Last 7 days" },
    { key: "30d", label: "Last 30 days" },
    { key: "90d", label: "Last 90 days" },
];

/**
 * Plant Conveyor Systems — branch tab for the Management Portal.
 * Split by RBTG / GTRB. Shows per-conveyor Utilisation + Reliability speedo
 * gauges, currently active routes on the live vessel, and an admin route /
 * conveyor manager.
 */
export default function ConveyorSystems({ user }) {
    const [division, setDivision] = useState("RBTG");
    const [period, setPeriod] = useState("24h");
    const [overview, setOverview] = useState(null);
    const [routes, setRoutes] = useState([]);
    const [activeRoutes, setActiveRoutes] = useState([]);
    const [breakdowns, setBreakdowns] = useState([]);
    const [activeVessel, setActiveVessel] = useState(null);
    const [openEquipmentId, setOpenEquipmentId] = useState(null);
    const [openEquipmentTab, setOpenEquipmentTab] = useState("performance");
    const isAdmin = user?.role === "admin" || user?.role === "manager" || user?.role === "plant_manager";
    const canInput = user?.role === "admin" || user?.role === "manager";
    const isElectrician = user?.role === "electrician" || user?.role === "admin" || user?.role === "manager";
    const openEquipment = (id, initialTab = "performance") => {
        setOpenEquipmentTab(initialTab);
        setOpenEquipmentId(id);
    };

    const load = useCallback(async () => {
        // Use allSettled so a single 403/network blip doesn't wipe the whole grid
        try {
            const vRes = await api.get("/vessels", { params: { status: "active" } }).catch(() => ({ data: [] }));
            const v = (vRes.data || [])[0] || null;
            setActiveVessel(v);

            const results = await Promise.allSettled([
                api.get("/conveyors/metrics/overview", { params: { division, period } }),
                api.get("/conveyor-routes", { params: { division } }),
                api.get("/conveyor-breakdowns", { params: { division } }),
                v ? api.get(`/vessels/${v.id}/conveyor-routes`) : Promise.resolve({ data: [] }),
            ]);
            const [ovRes, rRes, bRes, arRes] = results;
            if (ovRes.status === "fulfilled") setOverview(ovRes.value.data);
            else console.warn("conveyor overview failed:", ovRes.reason?.message);
            setRoutes(rRes.status === "fulfilled" ? (rRes.value.data || []) : []);
            setBreakdowns(bRes.status === "fulfilled" ? (bRes.value.data || []) : []);
            setActiveRoutes(arRes.status === "fulfilled" ? (arRes.value.data || []).filter((s) => s.active) : []);
        } catch (err) {
            console.warn("Conveyor load failed:", err?.message || err);
        }
    }, [division, period]);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    return (
        <div className="space-y-5" data-testid="conveyor-systems-branch">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="uppercase-label text-[10px] text-[var(--grindrod-red)] font-semibold tracking-[0.14em]">Plant Operations</div>
                    <h2 className="font-display font-black text-2xl text-[var(--grindrod-navy)]">Equipment Performance</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Live Availability · Utilisation · Reliability · MTBF per equipment. Click any tile for the full performance dashboard. Admins &amp; Planners can log daily data and set targets.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Period</label>
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs"
                        data-testid="conveyor-period-select"
                    >
                        {PERIODS.map((p) => (
                            <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {isElectrician && (
                <ElectricianShortcuts onOpen={openEquipment} />
            )}

            <Tabs value={division} onValueChange={setDivision} className="w-full" data-testid="conveyor-division-tabs">
                <TabsList className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-sm mb-4" data-testid="conveyor-division-tabs-list">
                    <TabsTrigger
                        value="RBTG"
                        className="data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-xs uppercase tracking-wide font-display font-bold py-2"
                        data-testid="conveyor-tab-RBTG"
                    >
                        RBTG
                    </TabsTrigger>
                    <TabsTrigger
                        value="GTRB"
                        className="data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-xs uppercase tracking-wide font-display font-bold py-2"
                        data-testid="conveyor-tab-GTRB"
                    >
                        GTRB
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="RBTG" className="mt-0">
                    <ConveyorDivisionView
                        division="RBTG"
                        overview={overview}
                        routes={routes}
                        activeRoutes={activeRoutes}
                        breakdowns={breakdowns}
                        activeVessel={activeVessel}
                        isAdmin={isAdmin}
                        period={period}
                        onChanged={load}
                        onOpenEquipment={openEquipment}
                    />
                </TabsContent>
                <TabsContent value="GTRB" className="mt-0">
                    <ConveyorDivisionView
                        division="GTRB"
                        overview={overview}
                        routes={routes}
                        activeRoutes={activeRoutes}
                        breakdowns={breakdowns}
                        activeVessel={activeVessel}
                        isAdmin={isAdmin}
                        period={period}
                        onChanged={load}
                        onOpenEquipment={openEquipment}
                    />
                </TabsContent>
            </Tabs>
            {openEquipmentId && (
                <EquipmentDashboardModal
                    equipmentId={openEquipmentId}
                    onClose={() => { setOpenEquipmentId(null); load(); }}
                    canInput={canInput}
                    initialTab={openEquipmentTab}
                />
            )}
        </div>
    );
}


function ConveyorDivisionView({ division, overview, routes, activeRoutes, breakdowns, activeVessel, isAdmin, period, onChanged, onOpenEquipment }) {
    const conveyors = overview?.conveyors || [];
    const empty = conveyors.length === 0;

    return (
        <div className="space-y-5" data-testid={`conveyor-division-${division}`}>
            <ActiveRouteStrip activeVessel={activeVessel} activeRoutes={activeRoutes} division={division} />

            {empty ? (
                <div className="g-card p-8 text-center" data-testid="conveyor-empty-state">
                    <Wrench size={32} weight="fill" className="mx-auto text-slate-300" />
                    <div className="font-display font-bold text-[var(--grindrod-navy)] mt-2">No conveyors registered for {division}</div>
                    <div className="text-xs text-slate-500 mt-1">
                        {isAdmin
                            ? "Add the conveyors operating in this division below to start tracking utilisation."
                            : "An admin needs to register the conveyors for this division before live metrics will appear."}
                    </div>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="conveyor-grid">
                    {conveyors.map((c) => (
                        <ConveyorTile key={c.id} conveyor={c} period={period} breakdowns={breakdowns.filter((b) => b.conveyor_id === c.id)} onOpen={() => onOpenEquipment && onOpenEquipment(c.id, "performance")} />
                    ))}
                </div>
            )}

            <BreakdownLog breakdowns={breakdowns} isAdmin={isAdmin} onChanged={onChanged} />

            {isAdmin && (
                <div className="grid gap-5 md:grid-cols-2">
                    <ConveyorAdmin division={division} conveyors={conveyors} onChanged={onChanged} />
                    <RouteAdmin division={division} routes={routes} conveyors={conveyors} onChanged={onChanged} />
                </div>
            )}
        </div>
    );
}


function ConveyorTile({ conveyor, period, breakdowns, onOpen }) {
    const accent =
        conveyor.utilisation_pct >= 85 ? "#00A344" : conveyor.utilisation_pct >= 60 ? "#FFB300" : "#D6202F";
    const openBd = breakdowns.find((b) => b.active);
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => {
                if (onOpen) onOpen();
            }}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpen) onOpen(); }}
            className="g-card overflow-hidden shadow-sm text-left hover:shadow-md hover:border-[var(--grindrod-navy)] transition-all cursor-pointer w-full"
            data-testid={`conveyor-tile-${conveyor.name}`}
        >
            <div
                className="px-4 py-2.5 border-b-[3px] flex items-center gap-2"
                style={{ borderColor: accent }}
            >
                <div className="font-display font-bold text-[var(--grindrod-navy)] uppercase tracking-wide tabular flex-1">
                    {conveyor.name}
                </div>
                {openBd && (
                    <span className="g-pill g-pill-red animate-pulse" style={{ fontSize: 9, padding: "2px 6px" }}>
                        DOWN
                    </span>
                )}
                <ChartLineUp size={14} className="text-slate-400" weight="bold" />
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
                <ConveyorGauge value={conveyor.utilisation_pct} label="UTILISATION" lowBand={60} highBand={85} size={170} />
                <ConveyorGauge value={conveyor.reliability_pct} label="RELIABILITY" lowBand={90} highBand={98} size={170} />
            </div>
            <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100 grid grid-cols-3 gap-2">
                <div>
                    <div className="uppercase tracking-wide text-[9px]">Active</div>
                    <div className="font-display font-bold text-[var(--grindrod-navy)] tabular">{conveyor.active_hours.toFixed(1)}h</div>
                </div>
                <div>
                    <div className="uppercase tracking-wide text-[9px]">Breakdown</div>
                    <div className="font-display font-bold text-[var(--grindrod-red)] tabular">{conveyor.breakdown_hours.toFixed(1)}h</div>
                </div>
                <div>
                    <div className="uppercase tracking-wide text-[9px]">Period</div>
                    <div className="font-display font-bold text-[var(--grindrod-navy)]">{period}</div>
                </div>
            </div>
        </div>
    );
}


function ActiveRouteStrip({ activeVessel, activeRoutes, division }) {
    const relevant = (activeRoutes || []).filter((r) => (r.division || "RBTG") === division);
    if (!activeVessel) {
        return (
            <div className="g-card p-3 text-xs text-slate-500 flex items-center gap-2" data-testid="conveyor-no-vessel">
                <Warning size={14} className="text-slate-400" />
                <span>No active vessel — utilisation will only reflect historical activity for the selected period.</span>
            </div>
        );
    }
    return (
        <div className="g-card p-3" data-testid="conveyor-active-route-strip">
            <div className="flex items-center gap-3 flex-wrap">
                <div>
                    <div className="uppercase-label text-[10px] text-[var(--grindrod-red)] font-semibold">Active vessel</div>
                    <div className="font-display font-bold text-sm text-[var(--grindrod-navy)]">
                        {activeVessel.vessel_name} · {activeVessel.client_name}
                    </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="uppercase-label text-[10px] text-slate-500 font-semibold">Currently routed</div>
                    {relevant.length === 0 ? (
                        <div className="text-xs text-slate-400">No route active for {division} yet — supervisor must select one in the vessel dashboard.</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5 mt-0.5">
                            {relevant.map((r) => (
                                <span key={r.id} className="px-2 py-0.5 rounded-sm bg-[var(--grindrod-green-bg)] border border-[var(--grindrod-green)] text-[var(--grindrod-green)] text-[11px] font-semibold tabular" data-testid={`active-route-pill-${r.route_name}`}>
                                    {r.route_name}{r.description ? ` · ${r.description}` : ""}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function BreakdownLog({ breakdowns, isAdmin, onChanged }) {
    const sorted = useMemo(() => [...breakdowns].sort((a, b) => (b.start_time || "").localeCompare(a.start_time || "")), [breakdowns]);

    const closeBd = async (id) => {
        if (!window.confirm("Mark breakdown as resolved (end now)?")) return;
        try {
            await api.post(`/conveyor-breakdowns/${id}/end`, {});
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this breakdown record?")) return;
        try {
            await api.delete(`/conveyor-breakdowns/${id}`);
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="g-card shadow-sm" data-testid="conveyor-breakdown-log">
            <div className="g-card-header-dark">
                <Warning size={16} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide text-sm">Breakdown Log</div>
                <span className="ml-auto text-xs font-semibold tabular">{sorted.length}</span>
            </div>
            {sorted.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400" data-testid="conveyor-no-breakdowns">No breakdowns recorded.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="conveyor-breakdown-table">
                        <thead className="bg-slate-50 text-slate-500 uppercase">
                            <tr className="text-left">
                                <th className="px-3 py-2">Conveyor</th>
                                <th className="px-3 py-2">Start</th>
                                <th className="px-3 py-2">End</th>
                                <th className="px-3 py-2">Reason</th>
                                <th className="px-3 py-2">Accountable</th>
                                <th className="px-3 py-2">Logged by</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((b) => (
                                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`breakdown-row-${b.id}`}>
                                    <td className="px-3 py-2 font-semibold text-[var(--grindrod-navy)]">{b.conveyor_name}</td>
                                    <td className="px-3 py-2 tabular text-slate-600">{b.start_time ? new Date(b.start_time).toLocaleString() : "—"}</td>
                                    <td className="px-3 py-2 tabular">
                                        {b.end_time ? (
                                            <span className="text-slate-600">{new Date(b.end_time).toLocaleString()}</span>
                                        ) : (
                                            <span className="g-pill g-pill-red animate-pulse" style={{ fontSize: 9, padding: "2px 6px" }}>ONGOING</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">{b.reason}</td>
                                    <td className="px-3 py-2">
                                        <span className={`g-pill ${b.accountability === "external" ? "" : ""}`} style={{ fontSize: 9, padding: "2px 6px", background: b.accountability === "external" ? "#CCFBF1" : "#E0E7FF", color: b.accountability === "external" ? "#0F766E" : "var(--grindrod-navy)", border: `1px solid ${b.accountability === "external" ? "#14B8A6" : "var(--grindrod-navy)"}` }}>
                                            {b.accountability === "external" ? "Transnet" : "Internal"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{b.logged_by_name}</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex gap-1">
                                            {!b.end_time && (
                                                <button onClick={() => closeBd(b.id)} className="btn-outline !py-1 !px-2 text-[10px]" data-testid={`end-breakdown-${b.id}`}>End</button>
                                            )}
                                            {isAdmin && (
                                                <button onClick={() => remove(b.id)} className="btn-outline !py-1 !px-2 text-[10px] !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]" data-testid={`delete-breakdown-${b.id}`}>
                                                    <Trash size={11} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function ConveyorAdmin({ division, conveyors, onChanged }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [reseeding, setReseeding] = useState(false);

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            await api.post("/conveyors", { division, name: name.trim() });
            setName("");
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };
    const reseed = async () => {
        if (!window.confirm("Recreate any missing default equipment (Coal Tippler, KBF01-04, NCV01-05, KCV05-08, Overland, Rock Tippler, NCV101/201/202)? Existing items will be untouched.")) return;
        setReseeding(true);
        try {
            const r = await api.post("/conveyors/reseed");
            alert(`Equipment reseeded. RBTG: ${r.data.rbtg} · GTRB: ${r.data.gtrb}`);
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Reseed failed");
        } finally {
            setReseeding(false);
        }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this conveyor? Routes referencing it will keep the name but no metrics will collect.")) return;
        try {
            await api.delete(`/conveyors/${id}`);
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="g-card shadow-sm" data-testid="conveyor-admin">
            <div className="g-card-header-dark">
                <div className="font-display font-bold uppercase tracking-wide text-sm">Conveyors ({division})</div>
                <span className="ml-auto text-xs font-semibold tabular">{conveyors.length}</span>
                <button onClick={reseed} disabled={reseeding} className="ml-2 text-[10px] uppercase tracking-wide bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded-sm disabled:opacity-50" data-testid="reseed-equipment-btn" title="Recreate any missing default equipment">
                    {reseeding ? "Reseeding…" : "Reseed equipment"}
                </button>
            </div>
            <ul className="divide-y divide-slate-100 max-h-[260px] overflow-y-auto">
                {conveyors.map((c) => (
                    <li key={c.id} className="px-4 py-2 flex items-center text-sm" data-testid={`conveyor-row-${c.name}`}>
                        <span className="flex-1 font-semibold text-[var(--grindrod-navy)] tabular">{c.name}</span>
                        <button onClick={() => del(c.id)} className="btn-outline !py-1 !px-2 text-[10px] !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]" data-testid={`delete-conveyor-${c.name}`}>
                            <Trash size={11} />
                        </button>
                    </li>
                ))}
                {conveyors.length === 0 && (
                    <li className="px-4 py-6 text-center text-xs text-slate-400">No conveyors yet.</li>
                )}
            </ul>
            <form onSubmit={add} className="px-4 py-3 border-t border-slate-100 flex gap-2" data-testid="conveyor-add-form">
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={`New ${division} conveyor name`}
                    className="flex-1 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                    data-testid="new-conveyor-name-input"
                />
                <button type="submit" disabled={busy} className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid="add-conveyor-btn">
                    <Plus size={12} weight="bold" /> Add
                </button>
            </form>
        </div>
    );
}


function RouteAdmin({ division, routes, conveyors, onChanged }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [picks, setPicks] = useState([]);
    const [busy, setBusy] = useState(false);

    const toggle = (cName) =>
        setPicks((prev) => (prev.includes(cName) ? prev.filter((x) => x !== cName) : [...prev, cName]));

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim() || picks.length === 0) {
            alert("Provide a route name and at least one conveyor");
            return;
        }
        setBusy(true);
        try {
            await api.post("/conveyor-routes", {
                division,
                name: name.trim(),
                description: desc.trim(),
                conveyors: picks,
            });
            setName(""); setDesc(""); setPicks([]);
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this route?")) return;
        try {
            await api.delete(`/conveyor-routes/${id}`);
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="g-card shadow-sm" data-testid="route-admin">
            <div className="g-card-header-dark">
                <div className="font-display font-bold uppercase tracking-wide text-sm">Routes ({division})</div>
                <span className="ml-auto text-xs font-semibold tabular">{routes.length}</span>
            </div>
            <ul className="divide-y divide-slate-100 max-h-[260px] overflow-y-auto">
                {routes.map((r) => (
                    <li key={r.id} className="px-4 py-2 flex items-start gap-3 text-sm" data-testid={`route-row-${r.name}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[var(--grindrod-navy)] flex items-center gap-2">
                                <span className="tabular">{r.name}</span>
                                {r.description && <span className="text-xs text-slate-500 truncate">— {r.description}</span>}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {r.conveyors.map((c) => (
                                    <span key={c} className="px-1.5 py-0.5 text-[10px] rounded-sm border border-slate-300 bg-slate-50 tabular">{c}</span>
                                ))}
                            </div>
                        </div>
                        <button onClick={() => del(r.id)} className="btn-outline !py-1 !px-2 text-[10px] !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]" data-testid={`delete-route-${r.name}`}>
                            <Trash size={11} />
                        </button>
                    </li>
                ))}
                {routes.length === 0 && (
                    <li className="px-4 py-6 text-center text-xs text-slate-400">No routes yet.</li>
                )}
            </ul>
            <form onSubmit={add} className="px-4 py-3 border-t border-slate-100 space-y-2" data-testid="route-add-form">
                <div className="grid grid-cols-2 gap-2">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Route name (e.g. Route 5)"
                        className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                        data-testid="new-route-name-input"
                    />
                    <input
                        type="text"
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        placeholder="Description (optional)"
                        className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                        data-testid="new-route-desc-input"
                    />
                </div>
                <div className="flex flex-wrap gap-1" data-testid="new-route-conveyor-picks">
                    {conveyors.map((c) => {
                        const on = picks.includes(c.name);
                        return (
                            <button
                                type="button"
                                key={c.id}
                                onClick={() => toggle(c.name)}
                                className={`px-2 py-1 text-[11px] rounded-sm border ${on ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}
                                data-testid={`route-pick-${c.name}`}
                            >
                                {c.name}
                            </button>
                        );
                    })}
                </div>
                <button type="submit" disabled={busy} className="btn-primary text-xs w-full inline-flex items-center justify-center gap-1 disabled:opacity-40" data-testid="add-route-btn">
                    <Plus size={12} weight="bold" /> Add route
                </button>
            </form>
        </div>
    );
}


function ElectricianShortcuts({ onOpen }) {
    const [conveyors, setConveyors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchList = useCallback(async (manual = false) => {
        if (manual) setRefreshing(true);
        try {
            setError(null);
            // Cache-bust query so no CDN / browser can serve a stale empty list
            const bust = `_t=${Date.now()}`;
            const [rbtg, gtrb] = await Promise.allSettled([
                api.get(`/conveyors?division=RBTG&${bust}`),
                api.get(`/conveyors?division=GTRB&${bust}`),
            ]);
            const items = [];
            if (rbtg.status === "fulfilled") items.push(...(rbtg.value.data || []));
            else console.warn("RBTG failed:", rbtg.reason?.message);
            if (gtrb.status === "fulfilled") items.push(...(gtrb.value.data || []));
            else console.warn("GTRB failed:", gtrb.reason?.message);
            setConveyors(items);
            if (items.length === 0) {
                setError("No equipment was returned. Try Refresh, or ask your admin to reseed.");
            }
        } catch (err) {
            console.warn("ElectricianShortcuts load failed:", err?.message);
            setError(err?.message || "Failed to load equipment list");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchList(false); }, [fetchList]);

    return (
        <div
            className="rounded-md border border-cyan-400/20 p-3 sm:p-4"
            style={{ background: "linear-gradient(135deg, #050A1F 0%, #0B1430 100%)" }}
            data-testid="electrician-shortcuts"
        >
            <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
                <div className="flex items-center gap-2">
                    <MagicWand size={16} weight="duotone" className="text-cyan-300" />
                    <div>
                        <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-xs uppercase">Electrician Shortcuts</div>
                        <div className="text-[10px] text-slate-400">{conveyors.length > 0 ? `${conveyors.length} equipment items · ` : ""}Jump straight into AI Fault Finding or the Fault Library.</div>
                    </div>
                </div>
                <button
                    onClick={() => fetchList(true)}
                    disabled={refreshing}
                    className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-[10px] px-2.5 py-1 rounded-sm inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="shortcuts-refresh-btn"
                >
                    <ArrowsClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
                </button>
            </div>
            {loading ? (
                <div className="text-slate-400 text-xs italic py-2">Loading equipment list…</div>
            ) : conveyors.length === 0 ? (
                <div className="text-amber-200 text-xs py-3 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 space-y-2" data-testid="shortcuts-empty">
                    <div className="font-bold">No equipment loaded yet.</div>
                    <div>This can happen on a fresh deployment before the equipment list reaches your device. Tap <span className="font-bold">Refresh</span> above. If still empty after a few attempts, ask an admin to press <span className="font-mono bg-black/30 px-1 rounded-sm">Reseed equipment</span> in the admin tools.</div>
                    {error && <div className="text-[10px] text-amber-300/70 mt-1">Detail: {error}</div>}
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5" data-testid="electrician-shortcut-grid">
                    {conveyors.map((c) => (
                        <div
                            key={c.id}
                            className="rounded-sm border border-cyan-400/15 bg-white/5 hover:bg-white/10 transition-colors overflow-hidden"
                            data-testid={`shortcut-${c.name}`}
                        >
                            <div className="px-2.5 py-1.5 text-white font-display font-bold uppercase tracking-wide text-[11px] truncate border-b border-cyan-400/10">
                                <span className="text-[9px] text-slate-400 font-normal normal-case tracking-normal mr-1">{c.division}</span>
                                {c.name}
                            </div>
                            <div className="grid grid-cols-2 divide-x divide-cyan-400/10">
                                <button
                                    onClick={() => onOpen(c.id, "ai")}
                                    className="px-1.5 py-1.5 text-cyan-200 hover:bg-cyan-400/15 inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide font-bold"
                                    data-testid={`shortcut-ai-${c.name}`}
                                >
                                    <ChatCircleDots size={11} weight="bold" /> AI Chat
                                </button>
                                <button
                                    onClick={() => onOpen(c.id, "faults")}
                                    className="px-1.5 py-1.5 text-amber-200 hover:bg-amber-400/15 inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide font-bold"
                                    data-testid={`shortcut-faults-${c.name}`}
                                >
                                    <Lightning size={11} weight="bold" /> Faults
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
